import React, { useState, useEffect, useMemo } from 'react';
import API from "../../utils/API";
import Swal from "sweetalert2";

const POSITION_ORDER = ['Chão', '1/2 Elevação', 'Elevado', 'Chão / Elevado', 'Chão / 1/2 Elevação', 'Sem posição'];

function groupByPosition(items) {
    const groups = {};
    for (const item of items) {
        const pos = item.position || 'Sem posição';
        if (!groups[pos]) groups[pos] = [];
        groups[pos].push(item);
    }
    // Sort groups by POSITION_ORDER
    const sorted = {};
    for (const pos of POSITION_ORDER) {
        if (groups[pos]) sorted[pos] = groups[pos];
    }
    // Add any remaining positions not in the predefined order
    for (const pos of Object.keys(groups)) {
        if (!sorted[pos]) sorted[pos] = groups[pos];
    }
    return sorted;
}

export default function ManageChecklistItems() {
    const [availableItems, setAvailableItems] = useState([]);
    const [userItems, setUserItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!localStorage.getItem('yc_usr_dt_ls')) {
            window.location.href = '/login';
        }

        const loadData = async () => {
            const user = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
            let company_id = user.company_id;
            setIsLoading(true);
            try {
                const availableResponse = await API.getAllChecklistItems(API.cancelToken());
                setAvailableItems(availableResponse.data);

                const userResponse = await API.getUserChecklistItems(API.cancelToken(), company_id);
                setUserItems(userResponse.data);
            } catch (error) {
                console.error('Error loading data:', error);
                Swal.fire({
                    title: 'Erro',
                    text: 'Não foi possível carregar os itens do checklist',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
        if (user && ["MECANICO", "COMPRADOR"].includes(user.role)) {
            window.location.href = '/services';
        }
    }, []);

    const selectedIds = useMemo(
        () => new Set(userItems.map(i => i.checklist_items_id)),
        [userItems]
    );

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return availableItems;
        const term = searchTerm.toLowerCase();
        return availableItems.filter(item =>
            item.checklist_item.toLowerCase().includes(term)
        );
    }, [availableItems, searchTerm]);

    const groupedItems = useMemo(() => groupByPosition(filteredItems), [filteredItems]);

    const handleToggleItem = async (item) => {
        if (item.is_mandatory) {
            Swal.fire({
                title: 'Item obrigatório',
                text: 'Este item é obrigatório e não pode ser removido do checklist.',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        const user = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
        let company_id = user.company_id;
        const isItemSelected = selectedIds.has(item.checklist_items_id);

        try {
            if (isItemSelected) {
                await API.deleteUserChecklistItem(API.cancelToken(), item.checklist_items_id);
                setUserItems(prev => prev.filter(ui => ui.checklist_items_id !== item.checklist_items_id));
            } else {
                await API.addUserChecklistItems(API.cancelToken(), {
                    user_id: company_id,
                    checklist_item_ids: [item.checklist_items_id]
                });
                setUserItems(prev => [...prev, item]);
            }
        } catch (error) {
            console.error('Error updating items:', error);
            Swal.fire({
                title: 'Erro',
                text: 'Não foi possível atualizar os itens do checklist',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleBulkToggle = async (groupItems, action) => {
        // Mandatory items are skipped from bulk actions (cannot be removed,
        // already always included on the backend; harmless to skip on add too).
        const targetItems = action === 'remove'
            ? groupItems.filter(i => !i.is_mandatory)
            : groupItems;
        const ids = targetItems.map(i => i.checklist_items_id);
        if (ids.length === 0) return;

        try {
            await API.bulkToggleChecklistItems(API.cancelToken(), {
                action,
                checklist_item_ids: ids
            });

            if (action === 'add') {
                setUserItems(prev => {
                    const existingIds = new Set(prev.map(i => i.checklist_items_id));
                    const newItems = targetItems.filter(i => !existingIds.has(i.checklist_items_id));
                    return [...prev, ...newItems];
                });
            } else {
                const removeSet = new Set(ids);
                setUserItems(prev => prev.filter(i => !removeSet.has(i.checklist_items_id)));
            }
        } catch (error) {
            console.error('Error bulk toggling:', error);
            Swal.fire({
                title: 'Erro',
                text: 'Não foi possível atualizar os itens',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleAddCustomItem = () => {
        Swal.fire({
            title: 'Adicionar Item Personalizado',
            html:
                '<input id="swal-item-name" class="swal2-input" placeholder="Nome do item">' +
                '<select id="swal-item-position" class="swal2-select" style="margin-top:12px;padding:8px;border:1px solid #d5d5d5;border-radius:4px;width:70%;">' +
                '<option value="">Sem posição</option>' +
                '<option value="Chão">Chão</option>' +
                '<option value="1/2 Elevação">1/2 Elevação</option>' +
                '<option value="Elevado">Elevado</option>' +
                '</select>' +
                '<div style="margin-top:14px;text-align:left;padding:0 12px;">' +
                '<div style="font-size:13px;color:#555;margin-bottom:6px;">Tipo de resposta</div>' +
                '<label style="display:flex;align-items:center;gap:6px;font-size:14px;margin-bottom:4px;">' +
                '<input type="radio" name="swal-answer-type" value="STATUS" checked> Bom / Regular / Ruim' +
                '</label>' +
                '<label style="display:flex;align-items:center;gap:6px;font-size:14px;">' +
                '<input type="radio" name="swal-answer-type" value="YES_NO"> Sim / Não (com descrição se Sim)' +
                '</label>' +
                '</div>',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Adicionar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#389E0D',
            preConfirm: () => {
                const name = document.getElementById('swal-item-name').value.trim();
                const position = document.getElementById('swal-item-position').value || null;
                const answerTypeEl = document.querySelector('input[name="swal-answer-type"]:checked');
                const answer_type = answerTypeEl ? answerTypeEl.value : 'STATUS';
                if (!name) {
                    Swal.showValidationMessage('Nome do item é obrigatório');
                    return false;
                }
                return { checklist_item: name, position, answer_type };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await API.addCustomChecklistItem(API.cancelToken(), result.value);
                    const newItem = {
                        checklist_items_id: res.data.checklist_items_id,
                        checklist_item: res.data.checklist_item,
                        position: res.data.position,
                        answer_type: res.data.answer_type || 'STATUS'
                    };
                    setAvailableItems(prev => [...prev, newItem]);
                    Swal.fire({
                        title: 'Sucesso',
                        text: 'Item adicionado com sucesso',
                        icon: 'success',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#389E0D'
                    });
                } catch (error) {
                    const msg = error.response?.data?.error || 'Não foi possível adicionar o item';
                    Swal.fire({
                        title: 'Erro',
                        text: msg,
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                }
            }
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#389E0D]"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col px-11 py-4 bg-white rounded-sm shadow-sm">
            <h1 className="text-2xl font-medium mb-6">Gerenciar Itens do Checklist</h1>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Buscar item..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent"
                />
                <button
                    onClick={handleAddCustomItem}
                    className="px-4 py-2 bg-[#389E0D] text-white rounded-md hover:bg-[#2A7B0A] transition-colors whitespace-nowrap"
                >
                    + Adicionar Item
                </button>
            </div>

            {Object.entries(groupedItems).map(([position, items]) => {
                const allSelected = items.every(i => selectedIds.has(i.checklist_items_id));
                const noneSelected = items.every(i => !selectedIds.has(i.checklist_items_id));

                return (
                    <div key={position} className="mb-6">
                        <div className="flex items-center justify-between mb-3 border-b border-gray-200 pb-2">
                            <h2 className="text-lg font-semibold text-gray-700">{position}</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleBulkToggle(items, 'add')}
                                    disabled={allSelected}
                                    className={`px-3 py-1 text-sm rounded ${allSelected
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-[#389E0D] text-white hover:bg-[#2A7B0A]'
                                    }`}
                                >
                                    Selecionar Todos
                                </button>
                                <button
                                    onClick={() => handleBulkToggle(items, 'remove')}
                                    disabled={noneSelected}
                                    className={`px-3 py-1 text-sm rounded ${noneSelected
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'border border-red-400 text-red-500 hover:bg-red-50'
                                    }`}
                                >
                                    Desmarcar Todos
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {items.map((item) => {
                                const isSelected = selectedIds.has(item.checklist_items_id) || item.is_mandatory;
                                const isMandatory = !!item.is_mandatory;
                                return (
                                    <div
                                        key={item.checklist_items_id}
                                        className={`p-4 rounded-md border transition-all ${
                                            isMandatory
                                                ? 'border-amber-300 bg-amber-50 cursor-not-allowed'
                                                : isSelected
                                                    ? 'border-[#389E0D] bg-green-50 cursor-pointer'
                                                    : 'border-gray-200 hover:border-[#389E0D] cursor-pointer'
                                        }`}
                                        onClick={() => handleToggleItem(item)}
                                        title={isMandatory ? "Item obrigatório — não pode ser removido" : undefined}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                                                isMandatory
                                                    ? 'bg-amber-500 border-amber-500'
                                                    : isSelected
                                                        ? 'bg-[#389E0D] border-[#389E0D]'
                                                        : 'border-gray-300'
                                            }`}>
                                                {isSelected && (
                                                    <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path d="M5 13l4 4L19 7"></path>
                                                    </svg>
                                                )}
                                            </div>
                                            <span className={`flex-1 ${isMandatory ? 'text-amber-900' : isSelected ? 'text-[#389E0D]' : 'text-gray-700'}`}>
                                                {item.checklist_item}
                                            </span>
                                            {isMandatory && (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                    </svg>
                                                    Obrigatório
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            <div className="flex justify-end mt-6">
                <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-[#389E0D] text-white rounded-md hover:bg-[#2A7B0A] transition-colors"
                >
                    Concluído
                </button>
            </div>
        </div>
    );
}
